import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import {
  Box, Typography, TextField, Button, IconButton, Paper, Chip,
  CircularProgress, Alert, Snackbar, Tooltip, LinearProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StarIcon from '@mui/icons-material/Star';
import PushPinIcon from '@mui/icons-material/PushPin';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Placeholder from '@tiptap/extension-placeholder';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import { IListningarEntry, IListningarMedia, IListningarEntryFormData, IListningarCategory } from './types';
import { LISTNINGAR_CATEGORIES } from './categories';
import { createEntry, updateEntry, uploadMedia, fetchCurrentWeek, fetchCategories, uploadThumbnail } from './listningarApi';
import { API_BASE_URL } from '../../config';
import { hasPermission } from '../../utils/permissionUtils';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import CropIcon from '@mui/icons-material/Crop';
import ListningarImagePositioner from './ListningarImagePositioner';

interface ListningarEntryEditorProps {
  entry?: IListningarEntry;
  onClose: () => void;
  onSaved: () => void;
  currentUser: any;
}

const ListningarEntryEditor: React.FC<ListningarEntryEditorProps> = ({
  entry,
  onClose,
  onSaved,
  currentUser,
}) => {
  const isEditing = !!entry;

  const [title, setTitle] = useState(entry?.title || '');
  const [category, setCategory] = useState(entry?.category || '');
  const [weekNumber, setWeekNumber] = useState(entry?.weekNumber || 0);
  const [year, setYear] = useState(entry?.year || 0);
  const [media, setMedia] = useState<IListningarMedia[]>(entry?.media || []);
  const [isHighlight, setIsHighlight] = useState(entry?.isHighlight || false);
  const [isPinned, setIsPinned] = useState(entry?.isPinned || false);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [dynamicCategories, setDynamicCategories] = useState<IListningarCategory[]>([]);
  const [positionerOpen, setPositionerOpen] = useState<number | null>(null);
  const canFeatureEntries = hasPermission(currentUser, 'listningar:update');

  useEffect(() => {
    fetchCategories().then(setDynamicCategories).catch(() => {});
  }, []);

  const availableCategories = dynamicCategories.length > 0 ? dynamicCategories : LISTNINGAR_CATEGORIES;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({ openOnClick: false }),
      Underline,
      Placeholder.configure({ placeholder: 'Beskriv vad ni har gjort...' }),
    ],
    content: DOMPurify.sanitize(entry?.description || '', { USE_PROFILES: { html: true } }),
  });

  useEffect(() => {
    if (!isEditing) {
      fetchCurrentWeek().then(({ weekNumber: w, year: y }) => {
        setWeekNumber(w);
        setYear(y);
      }).catch(() => {
        const now = new Date();
        setYear(now.getFullYear());
        setWeekNumber(getLocalISOWeek(now));
      });
    }
  }, [isEditing]);

  function getLocalISOWeek(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  }

  const handleFileUpload = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    setUploading(true);
    setUploadProgress(0);
    try {
      const uploaded = await uploadMedia(fileArray, (percent) => setUploadProgress(percent));
      setMedia(prev => [...prev, ...uploaded]);
      setSnackbar({ open: true, message: `${uploaded.length} fil(er) uppladdade`, severity: 'success' });
    } catch (err: any) {
      console.error('Upload failed:', err);
      setError('Kunde inte ladda upp filer');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files?.length) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const removeMedia = (index: number) => {
    setMedia(prev => prev.filter((_, i) => i !== index));
  };

  const handleCustomThumbnail = useCallback(async (mediaIndex: number, file: File) => {
    try {
      const thumbnailUrl = await uploadThumbnail(file);
      setMedia(prev => prev.map((m, i) => i === mediaIndex ? { ...m, thumbnailUrl } : m));
      setSnackbar({ open: true, message: 'Förhandsvisning uppdaterad', severity: 'success' });
    } catch (err) {
      console.error('Thumbnail upload failed:', err);
      setSnackbar({ open: true, message: 'Kunde inte ladda upp förhandsvisning', severity: 'error' });
    }
  }, []);

  const handlePasteForThumbnail = useCallback((mediaIndex: number, e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) {
          e.preventDefault();
          handleCustomThumbnail(mediaIndex, file);
          return;
        }
      }
    }
  }, [handleCustomThumbnail]);

  const handleSave = async () => {
    if (!title.trim()) {
      setError('Titel krävs');
      return;
    }
    if (!category) {
      setError('Välj en kategori');
      return;
    }
    if (!weekNumber || !year) {
      setError('Vecka och år krävs');
      return;
    }

    setSaving(true);
    setError(null);

    const formData: IListningarEntryFormData = {
      title: title.trim(),
      description: DOMPurify.sanitize(editor?.getHTML() || '', { USE_PROFILES: { html: true } }),
      category,
      weekNumber,
      year,
      media,
      isHighlight: canFeatureEntries ? isHighlight : false,
      isPinned: canFeatureEntries ? isPinned : false,
    };

    try {
      if (isEditing) {
        await updateEntry(entry!._id, formData);
        setSnackbar({ open: true, message: 'Inlägget har uppdaterats', severity: 'success' });
      } else {
        await createEntry(formData);
        setSnackbar({ open: true, message: 'Inlägget har skapats', severity: 'success' });
      }
      setTimeout(onSaved, 500);
    } catch (err: any) {
      console.error('Save failed:', err);
      setError(err?.response?.data?.error || 'Kunde inte spara inlägget');
    } finally {
      setSaving(false);
    }
  };

  const mediaUrl = (url: string) => {
    if (url.startsWith('http')) return url;
    return `${API_BASE_URL}${url}`;
  };

  const ToolbarButton = ({ icon, isActive, onClick, title: btnTitle }: {
    icon: React.ReactNode; isActive?: boolean; onClick: () => void; title: string;
  }) => (
    <Tooltip title={btnTitle}>
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          borderRadius: 1,
          bgcolor: isActive ? 'action.selected' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#fafafa',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          bgcolor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={onClose} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {isEditing ? 'Redigera inlägg' : 'Nytt inlägg'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Avbryt
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: '#072f53',
              '&:hover': { bgcolor: '#0a4175' },
              textTransform: 'none',
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {isEditing ? 'Spara ändringar' : 'Publicera'}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          py: 3,
        }}
      >
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Titel"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            placeholder="t.ex. Nytt säljblad - Vårsortimentet"
            sx={{ mb: 3 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Kategori *
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
            {availableCategories.map(cat => (
              <Chip
                key={cat.id}
                label={cat.label}
                variant={category === cat.id ? 'filled' : 'outlined'}
                onClick={() => setCategory(cat.id)}
                sx={{
                  fontWeight: category === cat.id ? 700 : 400,
                  bgcolor: category === cat.id ? cat.color : 'transparent',
                  color: category === cat.id ? '#fff' : 'text.primary',
                  borderColor: category === cat.id ? cat.color : 'divider',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: category === cat.id ? cat.color : `${cat.color}18`,
                  },
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="Vecka"
              type="number"
              value={weekNumber}
              onChange={(e) => setWeekNumber(Number(e.target.value))}
              inputProps={{ min: 1, max: 53 }}
              sx={{ width: 120 }}
              required
            />
            <TextField
              label="År"
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              inputProps={{ min: 2020, max: 2099 }}
              sx={{ width: 120 }}
              required
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Beskrivning
          </Typography>
          <Paper variant="outlined" sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
            {editor && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  gap: 0.3,
                  p: 0.75,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: '#fafafa',
                  alignItems: 'center',
                }}
              >
                <ToolbarButton
                  title="Fet"
                  icon={<FormatBoldIcon sx={{ fontSize: 18 }} />}
                  isActive={editor.isActive('bold')}
                  onClick={() => editor.chain().focus().toggleBold().run()}
                />
                <ToolbarButton
                  title="Kursiv"
                  icon={<FormatItalicIcon sx={{ fontSize: 18 }} />}
                  isActive={editor.isActive('italic')}
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                />
                <ToolbarButton
                  title="Understruken"
                  icon={<FormatUnderlinedIcon sx={{ fontSize: 18 }} />}
                  isActive={editor.isActive('underline')}
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                />
                <Box sx={{ width: '1px', height: 20, bgcolor: 'divider', mx: 0.5, flexShrink: 0 }} />
                <ToolbarButton
                  title="Punktlista"
                  icon={<FormatListBulletedIcon sx={{ fontSize: 18 }} />}
                  isActive={editor.isActive('bulletList')}
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                />
                <ToolbarButton
                  title="Numrerad lista"
                  icon={<FormatListNumberedIcon sx={{ fontSize: 18 }} />}
                  isActive={editor.isActive('orderedList')}
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                />
                <Box sx={{ width: '1px', height: 20, bgcolor: 'divider', mx: 0.5, flexShrink: 0 }} />
                <ToolbarButton
                  title="Länk"
                  icon={<LinkIcon sx={{ fontSize: 18 }} />}
                  isActive={editor.isActive('link')}
                  onClick={() => {
                    const url = window.prompt('URL:');
                    if (url) {
                      editor.chain().focus().setLink({ href: url }).run();
                    }
                  }}
                />
                <Box sx={{ width: '1px', height: 20, bgcolor: 'divider', mx: 0.5, flexShrink: 0 }} />
                <ToolbarButton
                  title="Ångra"
                  icon={<UndoIcon sx={{ fontSize: 18 }} />}
                  onClick={() => editor.chain().focus().undo().run()}
                />
                <ToolbarButton
                  title="Gör om"
                  icon={<RedoIcon sx={{ fontSize: 18 }} />}
                  onClick={() => editor.chain().focus().redo().run()}
                />
              </Box>
            )}

            <Box
              sx={{
                minHeight: 150,
                maxHeight: 300,
                overflowY: 'auto',
                p: 2,
                '& .ProseMirror': {
                  outline: 'none',
                  minHeight: 120,
                  '& p.is-editor-empty:first-of-type::before': {
                    content: 'attr(data-placeholder)',
                    color: '#aaa',
                    pointerEvents: 'none',
                    float: 'left',
                    height: 0,
                  },
                },
              }}
            >
              <EditorContent editor={editor} />
            </Box>
          </Paper>

          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Media (bilder & video)
          </Typography>

          <Paper
            variant="outlined"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            sx={{
              p: 3,
              mb: 2,
              borderRadius: 2,
              borderStyle: 'dashed',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
            onClick={() => document.getElementById('listningar-media-input')?.click()}
          >
            <input
              id="listningar-media-input"
              type="file"
              multiple
              accept="image/*,video/*,application/pdf"
              hidden
              onChange={(e) => {
                if (e.target.files?.length) {
                  handleFileUpload(e.target.files);
                  e.target.value = '';
                }
              }}
            />
            <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Dra & släpp filer här, eller klicka för att välja
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Stödjer: JPG, PNG, WebP, GIF, SVG, MP4, MOV, AVI, WebM, PDF (max 100 MB)
            </Typography>
          </Paper>

          {uploading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} sx={{ borderRadius: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Laddar upp... {uploadProgress}%
              </Typography>
            </Box>
          )}

          {media.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
              {media.map((m, i) => (
                <Box
                  key={i}
                  tabIndex={(m.type === 'pdf' || m.type === 'video') ? 0 : undefined}
                  onPaste={(m.type === 'pdf' || m.type === 'video') ? (e: React.ClipboardEvent) => handlePasteForThumbnail(i, e) : undefined}
                  sx={{
                    position: 'relative',
                    width: 120,
                    height: 120,
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: 'divider',
                    outline: 'none',
                    '&:focus': { borderColor: 'primary.main', boxShadow: '0 0 0 2px rgba(33,150,243,0.3)' },
                  }}
                >
                  {m.type === 'image' ? (
                    <img
                      src={mediaUrl(m.url)}
                      alt={m.caption || m.originalName || ''}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: m.objectPosition || 'center center' }}
                    />
                  ) : m.type === 'pdf' ? (
                    m.thumbnailUrl ? (
                      <img
                        src={mediaUrl(m.thumbnailUrl)}
                        alt={m.originalName || 'PDF'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: m.objectPosition || 'center center' }}
                      />
                    ) : (
                      <Box sx={{ width: '100%', height: '100%', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <PictureAsPdfIcon sx={{ color: '#E53935', fontSize: 36 }} />
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', mt: 0.5, color: 'text.secondary' }}>PDF</Typography>
                      </Box>
                    )
                  ) : (
                    m.thumbnailUrl || m.videoPreviewUrl ? (
                      <img
                        src={mediaUrl(m.thumbnailUrl || m.videoPreviewUrl || '')}
                        alt={m.originalName || 'Video'}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Box sx={{ width: '100%', height: '100%', bgcolor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PlayCircleIcon sx={{ color: '#fff', fontSize: 32 }} />
                      </Box>
                    )
                  )}
                  <Box sx={{ position: 'absolute', top: 2, right: 2, display: 'flex', gap: 0.3 }}>
                    {(m.type === 'image' || m.thumbnailUrl) && (
                      <Tooltip title="Justera bildposition">
                        <IconButton
                          size="small"
                          onClick={() => setPositionerOpen(i)}
                          sx={{
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            p: 0.3,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                          }}
                        >
                          <CropIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {(m.type === 'pdf' || m.type === 'video') && (
                      <Tooltip title="Byt förhandsvisning (välj bild eller klistra in)">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = 'image/*';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) handleCustomThumbnail(i, file);
                            };
                            input.click();
                          }}
                          sx={{
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: '#fff',
                            p: 0.3,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => removeMedia(i)}
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        p: 0.3,
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                  {m.originalName && (
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: 'rgba(0,0,0,0.6)',
                        color: '#fff',
                        px: 0.5,
                        py: 0.2,
                        fontSize: '0.6rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {m.originalName}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {canFeatureEntries && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Visningsalternativ
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Chip
                  icon={<StarIcon sx={{ color: isHighlight ? '#FFD700' : undefined }} />}
                  label="Veckans highlight"
                  variant={isHighlight ? 'filled' : 'outlined'}
                  onClick={() => setIsHighlight(!isHighlight)}
                  sx={{
                    fontWeight: isHighlight ? 700 : 400,
                    bgcolor: isHighlight ? '#FFF8E1' : 'transparent',
                    borderColor: isHighlight ? '#FFD700' : 'divider',
                    cursor: 'pointer',
                  }}
                />
                <Chip
                  icon={<PushPinIcon sx={{ color: isPinned ? '#FF9800' : undefined }} />}
                  label="Fäst inlägg"
                  variant={isPinned ? 'filled' : 'outlined'}
                  onClick={() => setIsPinned(!isPinned)}
                  sx={{
                    fontWeight: isPinned ? 700 : 400,
                    bgcolor: isPinned ? '#FFF3E0' : 'transparent',
                    borderColor: isPinned ? '#FF9800' : 'divider',
                    cursor: 'pointer',
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      {positionerOpen !== null && media[positionerOpen] && (
        <ListningarImagePositioner
          open
          imageUrl={mediaUrl(
            media[positionerOpen].type === 'image'
              ? media[positionerOpen].url
              : media[positionerOpen].thumbnailUrl || media[positionerOpen].url
          )}
          currentPosition={media[positionerOpen].objectPosition}
          onSave={(position) => {
            setMedia(prev => prev.map((m, i) => i === positionerOpen ? { ...m, objectPosition: position } : m));
            setPositionerOpen(null);
          }}
          onClose={() => setPositionerOpen(null)}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ListningarEntryEditor;
